import { useState } from 'react';
import { Link } from 'react-router-dom';
import { get, post } from '../api';
import { useBrand } from '../components/Brand';
import { Problems } from '../components/Field';
import { contractorTypeOptions } from '../contractorTypes';
import { describe } from '../errors';
import { useLoad } from '../hooks';

/** The checklists a visit can carry. Each assignment picks one by name. */
interface ChecklistRow {
  id: number;
  name: string;
  tasks: number;
  /** The types of work it is the default for. */
  job_types?: string[];
  used_by: number;
  state: string;
}

export function Checklists() {
  const brand = useBrand();
  const typeOptions = contractorTypeOptions(brand?.slug ?? '');
  const typeLabel = (v: string) => typeOptions.find((t) => t.value === v)?.label ?? v;
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const list = useLoad(() => get<{ items: ChecklistRow[] }>('/api/checklists'), [tick]);
  const items = Array.isArray(list.data?.items) ? list.data!.items : [];
  const active = items.filter((c) => c.state === 'active').length;

  async function toggle(c: ChecklistRow) {
    setProblems([]);
    try {
      await post(`/api/checklists/${c.id}/active`, { active: c.state !== 'active' });
      setTick((t) => t + 1);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <section className="stack" aria-labelledby="cl-h">
      <div className="row">
        <h1 id="cl-h">Checklists</h1>
        <Link className="button" to="/checklists/new">
          + Add checklist
        </Link>
      </div>
      <p className="muted">
        Each assignment picks a checklist by name, or gets the default for its type of work. Its
        tasks are copied onto a visit on the day, so changing one only affects visits still to come.
      </p>
      <Problems problems={problems} title="Could not do that." />
      {list.error && (
        <p role="alert" className="error">
          {list.error}
        </p>
      )}
      {items.length > 0 && (
        <p className="muted">
          {items.length} {items.length === 1 ? 'checklist' : 'checklists'} · {active} active ·{' '}
          {items.length - active} inactive
        </p>
      )}
      {list.data && items.length === 0 && (
        <div className="empty-state">
          <h2>No checklists yet.</h2>
          <p>Write the tasks once, then pick the checklist on any assignment that needs it.</p>
          <Link className="button" to="/checklists/new">
            Add the first checklist
          </Link>
        </div>
      )}
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Default for</th>
              <th>Tasks</th>
              <th>Used by</th>
              <th>Status</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/checklists/${c.id}`}>
                    <strong>{c.name}</strong>
                  </Link>
                </td>
                <td>
                  {(c.job_types ?? []).length > 0 ? c.job_types!.map(typeLabel).join(', ') : '—'}
                </td>
                <td>{c.tasks} tasks</td>
                <td>
                  {c.used_by} {c.used_by === 1 ? 'assignment' : 'assignments'}
                </td>
                <td>
                  <span className={c.state === 'active' ? 'pill ok' : 'pill'}>
                    {c.state === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="actions">
                  <Link className="button quiet-button" to={`/checklists/${c.id}`}>
                    Edit tasks<span className="sr-only"> for {c.name}</span>
                  </Link>
                  <button type="button" className="quiet" onClick={() => void toggle(c)}>
                    {c.state === 'active' ? 'Deactivate' : 'Reactivate'}
                    <span className="sr-only"> {c.name}</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
